import { useState, useEffect } from 'react';
import type { ProductFaqItem } from '@/mocks/productFaqs';

interface ProductFaqProps {
  items: ProductFaqItem[];
  productName: string;
  productSlug: string;
}

export default function ProductFaq({ items, productName, productSlug }: ProductFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const existing = document.getElementById('product-faq-schema');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'product-faq-schema';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('product-faq-schema');
      if (s) s.remove();
    };
  }, [items]);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section className="bg-gray-900 py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <span className="text-green-400 text-xs font-black uppercase tracking-widest mb-3 block">
            Dúvidas Comuns
          </span>
          <h2 className="text-white text-2xl md:text-3xl font-black uppercase tracking-tight mb-3">
            Perguntas Frequentes — {productName}
          </h2>
          <p className="text-gray-400 text-sm max-w-xl mx-auto">
            Tire suas dúvidas sobre este equipamento. Se precisar de mais informações, fale com um especialista via WhatsApp.
          </p>
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {items.map((faq, idx) => (
            <div
              key={idx}
              className="border border-gray-800 rounded-xl overflow-hidden"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                type="button"
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between gap-4 p-4 md:p-5 text-left cursor-pointer hover:bg-gray-800/50 transition-colors"
                aria-expanded={openIndex === idx}
              >
                <span
                  className="text-white text-sm md:text-base font-bold"
                  itemProp="name"
                >
                  {faq.question}
                </span>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <i
                    className={`ri-arrow-down-s-line text-green-400 text-lg transition-transform duration-200 ${openIndex === idx ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${openIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div
                  className="px-4 md:px-5 pb-4 md:pb-5 text-gray-400 text-sm leading-relaxed"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div itemProp="text">{faq.answer}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}